<template>
  <!-- Vuetify 3.1.14: validate-on принимает только ОДНО значение ('input' | 'blur' | 'submit'),
       комбинации не работают. 'blur' — проверка при уходе фокуса с поля -->
  <v-form
    ref="form"
    v-model="isFormValid"
    class="user-form"
    validate-on="blur"
    @submit.prevent="submit"
  >
    <UserFormSection icon="pi pi-user" title="Личные данные">
      <v-text-field
        :model-value="modelValue.lastname"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="50"
        :rules="[rules.required]"
        @update:model-value="updateField('lastname', $event)"
      >
        <template #label>Фамилия<span class="required-asterisk">*</span></template>
      </v-text-field>

      <v-text-field
        :model-value="modelValue.firstname"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="50"
        :rules="[rules.required]"
        @update:model-value="updateField('firstname', $event)"
      >
        <template #label>Имя<span class="required-asterisk">*</span></template>
      </v-text-field>

      <v-text-field
        :model-value="modelValue.middlename"
        label="Отчество"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="50"
        @update:model-value="updateField('middlename', $event)"
      />

      <v-radio-group
        :model-value="modelValue.gender"
        inline
        density="compact"
        hide-details="auto"
        class="gender-group"
        validate-on="input"
        :rules="[rules.required]"
        @update:model-value="updateField('gender', $event)"
      >
        <span class="gender-group__label"> Пол<span class="required-asterisk">*</span> </span>
        <v-radio label="Мужской" value="male" />
        <v-radio label="Женский" value="female" />
      </v-radio-group>

      <v-text-field
        :model-value="modelValue.birthDate"
        label="Дата рождения"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="10"
        inputmode="numeric"
        placeholder="дд.мм.гггг"
        :rules="[birthDateRule]"
        @update:model-value="updateField('birthDate', $event)"
      />
    </UserFormSection>

    <UserFormSection icon="pi pi-id-card" title="Контакты">
      <v-text-field
        :model-value="modelValue.email"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="100"
        :disabled="emailDisabled"
        :error-messages="emailServerError"
        :rules="[rules.required, rules.email]"
        @update:model-value="onEmailInput"
      >
        <template #label>E-mail<span class="required-asterisk">*</span></template>
      </v-text-field>

      <v-text-field
        v-mask="'+7 (###) ###-##-##'"
        :model-value="modelValue.phone"
        label="Телефон"
        variant="outlined"
        density="compact"
        hide-details="auto"
        inputmode="numeric"
        :rules="[phoneRule]"
        @update:model-value="updateField('phone', $event)"
      />
    </UserFormSection>

    <UserFormSection icon="pi pi-map-marker" title="Адрес">
      <v-text-field
        :model-value="modelValue.region"
        label="Регион"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="50"
        @update:model-value="updateField('region', $event)"
      />

      <v-text-field
        :model-value="modelValue.city"
        label="Населённый пункт"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="50"
        @update:model-value="updateField('city', $event)"
      />
    </UserFormSection>

    <UserFormSection icon="pi pi-briefcase" title="Работа">
      <v-text-field
        :model-value="modelValue.organization"
        label="Организация"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="100"
        @update:model-value="updateField('organization', $event)"
      />

      <v-text-field
        :model-value="modelValue.position"
        label="Должность"
        variant="outlined"
        density="compact"
        hide-details="auto"
        maxlength="100"
        @update:model-value="updateField('position', $event)"
      />
    </UserFormSection>

    <!-- место для невозможных к редактированию данных (идентификатор, роль и т.п.) -->
    <slot name="readonly" />

    <v-checkbox
      v-if="showAgreement"
      :model-value="modelValue.agreement"
      color="primary"
      hide-details="auto"
      validate-on="input"
      :rules="[(v) => v === true || 'Необходимо подтвердить согласие']"
      @update:model-value="updateField('agreement', $event)"
    >
      <template #label>
        <span class="text-body-2">
          Я даю согласие на обработку моих персональных данных в соответствии с
          <a href="/Пользовательское соглашение.pdf" target="_blank" rel="noopener noreferrer">
            Пользовательским соглашением
          </a>
          и
          <a href="/Политика конфиденциальности.pdf" target="_blank" rel="noopener noreferrer">
            Политикой конфиденциальности
          </a>
        </span>
      </template>
    </v-checkbox>

    <div :class="['user-form__actions', compactButtons && 'user-form__actions--compact']">
      <v-btn
        v-if="showCancel"
        variant="text"
        color="primary"
        :disabled="loading"
        @click="emit('cancel')"
      >
        {{ cancelLabel }}
      </v-btn>

      <v-btn
        color="primary"
        type="submit"
        :loading="loading"
        :disabled="loading || isFormValid === false"
      >
        {{ submitLabel }}
      </v-btn>
    </div>
  </v-form>
</template>

<script setup>
import { ref } from 'vue'
import { birthDateRule, phoneRule, userFormRules } from '@/utils/userForm'
import UserFormSection from '@/components/User/UserFormSection.vue'

const props = defineProps({
  // объект с полями: lastname, firstname, middlename, gender, birthDate, email,
  // phone, region, city, organization, position, agreement
  modelValue: {
    type: Object,
    required: true,
  },
  submitLabel: {
    type: String,
    default: 'Сохранить',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showAgreement: {
    type: Boolean,
    default: false,
  },
  showCancel: {
    type: Boolean,
    default: false,
  },
  cancelLabel: {
    type: String,
    default: 'Отмена',
  },
  // компактный режим кнопок: как «Закрыть»/«Изменить» в диалоге —
  // дефолтные для Vuetify высота 36px, шрифт 14px, радиус 4px.
  // В false — крупные кнопки (регистрация)
  compactButtons: {
    type: Boolean,
    default: false,
  },
  // временно дизейблим редактирование e-mail в профиле (в регистрации поле активно)
  emailDisabled: {
    type: Boolean,
    default: false,
  },
  // Важно: '' вместо null — Vuetify 3.1.x падает на :error-messages="null"
  // (обращение к .length у null внутри useValidation)
  emailServerError: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'update:emailServerError', 'submit', 'cancel'])

const rules = userFormRules

// валидность формы: v-form обновляет её при каждой перепроверке полей.
// Кнопка сабмита активна, пока ошибок нет (или все исправлены)
const isFormValid = ref(true)

const form = ref(null)

function updateField(field, value) {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function onEmailInput(value) {
  updateField('email', value)
  emit('update:emailServerError', '')
}

async function submit() {
  const { valid } = await form.value.validate()

  if (!valid) {
    return
  }

  emit('submit')
}

defineExpose({
  validate: () => form.value?.validate(),
  resetValidation: () => form.value?.resetValidation(),
})
</script>

<style lang="scss" scoped>
.user-form {
  width: 100%;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  // увеличенный зазор между смысловыми блоками (поверх gap формы)
  .form-section + .form-section {
    margin-top: 6px;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  :deep(.v-field__input) {
    font-size: 14px;
  }

  :deep(.v-field-label) {
    font-size: 13px;
  }

  // всплывающий (поднявшийся) label — чуть меньше обычного
  :deep(.v-field-label--floating) {
    font-size: 12px;
  }

  // сообщения валидации («Заполните поле» и т.п.) — размером с всплывший label
  // !important обязателен: глобальное правило из ProjectForm.vue (незаскопленный <style>)
  // ставит .v-input--error .v-messages div { font-size: 10px } на всё приложение
  :deep(.v-messages) {
    font-size: 12px !important;
  }

  :deep(.v-messages__message) {
    font-size: 12px !important;
    line-height: 16px;
  }
}

.user-form__actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;

  // Сохранить и Отмена — одинаковые по размеру и типографике,
  // различие только в цвете: «Сохранить» залита primary, «Отмена» без заливки
  .v-btn {
    flex: 1 1 auto;
    height: 52px;
    max-height: 56px;
    font-size: 18px;
    border-radius: 20px;
  }

  // компактный стиль как у кнопок «Закрыть»/«Изменить» в диалоге:
  // дефолтные для Vuetify высота 36px, шрифт 14px, радиус 4px
  &--compact {
    .v-btn {
      height: 36px;
      font-size: 14px;
      border-radius: 4px;
    }
  }
}

.required-asterisk {
  color: var(--error);
  margin-left: 4px;
}

.gender-group {
  :deep(.v-selection-control-group) {
    align-items: center;
    width: 100%;
    // Vuetify по умолчанию даёт 16px отступов сверху/снизу — убираем,
    // высоту рамки выравниваем с outlined-полями (32 + 2*7 + 2 = 48px)
    margin: 0;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    padding: 7px 16px;
    column-gap: 16px;
  }

  :deep(.v-selection-control) {
    min-height: 32px;
    cursor: pointer;

    // зазор между кружком и подписью делаем частью <label>:
    // margin не кликабелен, padding — кликабелен. Вся зона «кружок + пол»
    // становится единым кликабельным блоком, визуальная раскладка не меняется
    .v-label {
      margin-left: 0;
      padding-left: 12px;
    }
  }

  &.v-input--error {
    :deep(.v-selection-control-group) {
      border-color: var(--error);
    }
  }
}

.gender-group__label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 15px;
}
</style>
